import { useFetch } from "hooks/api";
import { useAppSelector } from "redux/hooks";
import type { Row as TableRow } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import useQueryParams from "hooks/useQueryParams";
import { useDates } from "dates/DatesContext";
import { useAcosOrRoas } from "utils/acosOrRoas";
import type { Instance } from "react-table-6";
import { keepPreviousData } from "@tanstack/react-query";
import useTargetBulkActions from "hooks/useTargetBulkActions";
import { useAddTargetsToAd } from "modules/advertising/api/useAddTargets";
import { buildMetric } from "views/merchandise/metrics";
import useSelectedMetrics from "components/custom/merchandise/hooks/useSelectedMetrics";
import { snakeCaseToSentenceCase } from "utils/formatText";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import Loading from "components/core/blocks/Loading";
import { Col, Nav, Row } from "react-bootstrap";
import MultiMetricChart from "components/custom/merchandise/MultiMetricChart";
import { css } from "../../../styled-system/css";
import ExpanderTable from "views/merchandise/tables/BulkActionsTable";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import { formatTargetText } from "utils/formatTargetText";
import TrellisLink from "components/core/basic/TrellisLink";
import { getDashboardTo } from "views/AdvertisingOverview";
import { TargetBadges } from "views/SearchTermReport";
import { Flex } from "../../../styled-system/jsx";
import { SEARCH_TERM_REPORT_MATCH_TYPE_FILTER } from "views/advertising/targetLifecycleFilters";
import { type GVAdvertisement } from "modules/advertising/types";
import { useAdvertisementReport, useTargetReport } from "views/SearchTermReport";

import type { ExpanderProps, ExpanderColumnSpec, ExpanderQueryData, KeywordsResponse, ExtractAsinFn } from "./types";
import { Checkbox } from "components/fields/base/Checkbox";

const QueryExpander: React.FC<ExpanderProps> = ({ dims, query, keyword_text, match_type, row, context }) => {
  const [selectedTargets, setSelectedTargets] = useState<string[] | number[]>([]);
  const [selectedMatchTypes, setSelectedMatchTypes] = useState<string[]>([]);

  const tableRef = useRef<Instance<unknown> | null>(null);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const [tab, setTab] = useQueryParams("etab", "keyword");
  const { start, end, preStart, preEnd, group } = useDates();

  const acosOrRoas = useAcosOrRoas();
  let filters = `date__gte:${start.format("YYYY-MM-DD")},date__lte:${end.format("YYYY-MM-DD")},marketplace:${marketPlace.marketPlace}`;
  if (dims === "query") {
    filters = `${filters},query:${query}`;
  } else {
    filters = `${filters},keyword_text:${keyword_text},match_type:${match_type}`;
  }

  const extractAsin: ExtractAsinFn = (string: string): string | null => {
    const asinRegex = /b0[a-z0-9]{8}/i;

    const matches = string.match(asinRegex);
    return matches?.length ? matches[0] : null;
  };

  let keywordText = dims === "query" ? query : keyword_text;
  const isAsin = !!extractAsin(String(keywordText ?? ""));

  if (isAsin) {
    const asin = extractAsin(String(keywordText ?? ""));
    if (asin !== null) {
      keywordText = asin;
    }
  }
  const { data: _data, isLoading } = useFetch<Record<string, unknown>[]>(
    ["searchTermReport", filters, group, start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")],
    `/api/data_report_v2/searchtermreport/stats/`,
    {
      dimensions: group,
      filters,
    },
    {
      placeholderData: keepPreviousData,
      staleTime: 1000 * 60 * 5,
      select: (res) => res.data,
    },
  );
  const data = useMemo(() => {
    return _data?.map((row) => {
      return {
        start_date:
          typeof row.date === "string" ? row.date
          : row.date instanceof Date ? row.date.toISOString().slice(0, 10)
          : "",
        ...row,
      };
    });
  }, [_data]);

  const { data: targetData, isLoading: isTargetDataLoading } = useTargetReport(
    row.original.adv_targets?.filter((k) => k),
    "target",
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    group,
  );

  const { data: keywordData, isLoading: isKeywordDataLoading } = useTargetReport(
    Array.isArray(row.original.adv_keywords) ? row.original.adv_keywords.filter((k) => k) : [],
    "keyword",
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    group,
  );

  const { bulkActionOptions } = useTargetBulkActions(
    selectedTargets,
    setSelectedTargets,
    tab,
    tab === "target" ? targetData : keywordData,
  );
  const { data: advertisementData, isLoading: isAdsDataLoading } = useAdvertisementReport();
  const addTargetsMutation = useAddTargetsToAd();
  const negateTargets = () => {
    for (let i = 0; i < selectedTargets.length; i++) {
      const data = tab === "target" ? targetData : keywordData;
      const rowDetails = (
        data as { keywords: { id: string; advertisement_id: string; ad_group_id: string }[] }
      ).keywords.find((target) => target.id === selectedTargets[i]);

      const body = {
        data: {
          advertisement: Number(rowDetails?.advertisement_id),
          ad_group: rowDetails?.ad_group_id,
          target_type: isAsin ? "product" : "keyword",
          channel: "amazon",
          action: "create_targets" as const,
          targets:
            isAsin ?
              {
                products: [String(keywordText)],
                targetTypes: ["negative_product"],
              }
            : [
                {
                  text: String(keywordText),
                  matchTypes: selectedMatchTypes,
                },
              ],
        },
      };

      addTargetsMutation.mutate(body);
    }
  };

  const getValidKeywordToNegate = (matchType: string, keywordText: string) => {
    const wordCount = keywordText.trim().split(/\s+/).filter(Boolean).length;
    const MAX_NEGATIVE_KEYWORD_PHRASE_LENGTH = 4;
    const MAX_NEGATIVE_KEYWORD_EXACT_LENGTH = 10;
    const MAX_KW_LENGTH = 80;

    if (matchType === "negative_phrase") {
      return keywordText.length <= MAX_KW_LENGTH && wordCount <= MAX_NEGATIVE_KEYWORD_PHRASE_LENGTH;
    }

    if (matchType === "negative_exact") {
      return keywordText.length <= MAX_KW_LENGTH && wordCount <= MAX_NEGATIVE_KEYWORD_EXACT_LENGTH;
    }

    return true;
  };

  const targetTextAccessor = dims === "query" ? "query" : "keyword_text";
  const isCategoryTarget = row.original[targetTextAccessor]?.includes("category=");

  const additionalBulkActions =
    isCategoryTarget ?
      []
    : [
        {
          label: `Negate Query ${isAsin ? "ASIN" : "Text"}`,
          action: () => negateTargets(),
          actionFormFields:
            isAsin ? null : (
              <Flex direction={"column"} alignItems={"center"}>
                <p>Select the match types you want to negate:</p>
                <div className={css({ paddingX: "large" })}>
                  {["negative_exact", "negative_phrase"].map((matchType) => (
                    <Checkbox
                      isDisabled={!getValidKeywordToNegate(matchType, keywordText ? String(keywordText) : "")}
                      key={matchType}
                      label={snakeCaseToSentenceCase(matchType)}
                      value={selectedMatchTypes.includes(matchType)}
                      onChange={(checked) =>
                        setSelectedMatchTypes((prev) =>
                          checked ? [...prev, matchType] : prev.filter((t) => t !== matchType),
                        )
                      }
                    />
                  ))}
                </div>
              </Flex>
            ),
          disableConfirmButton: isAsin ? false : selectedMatchTypes.length === 0,
          confirmationMessage: (
            <p className="fs-4 py-2">
              Are you sure you want to negate the {isAsin ? "ASIN" : "keyword"} "
              {isAsin ? String(keywordText).toUpperCase() : keywordText}"?
            </p>
          ),
        },
      ];

  const metrics = useMemo(
    () => [
      buildMetric(marketPlace, { total: data }, "ad_sales", "ad_sales_percent"),
      buildMetric(marketPlace, { total: data }, "ad_spend", "ad_spend_percent"),
      acosOrRoas(
        buildMetric(marketPlace, { total: data }, "acos", "acos_delta"),
        buildMetric(marketPlace, { total: data }, "roas", "roas_delta"),
      ),
      buildMetric(marketPlace, { total: data }, "clicks", "clicks_percent"),
      buildMetric(marketPlace, { total: data }, "impressions", "impressions_percent"),
      buildMetric(marketPlace, { total: data }, "ctr", "ctr_delta"),
      buildMetric(marketPlace, { total: data }, "conversion_rate", "conversion_rate_delta"),
      buildMetric(marketPlace, { total: data }, "cpc", "cpc_delta"),
      buildMetric(marketPlace, { total: data }, "viewable_impressions", "viewable_impressions_percent"),
      buildMetric(marketPlace, { total: data }, "viewability_rate", "viewability_rate_delta"),
      buildMetric(marketPlace, { total: data }, "view_click_through_rate", "view_click_through_rate_delta"),
    ],
    [marketPlace, data, acosOrRoas],
  );

  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["impressions", "ad_spend", "ad_sales"],
    "searchTermReportExpander",
  );

  const columns = useMemo<ExpanderColumnSpec[]>(() => {
    const perfCol = acosOrRoas({ key: "acos", compareKey: "acos_delta" }, { key: "roas", compareKey: "roas_delta" });
    const cols: ExpanderColumnSpec[] = [
      {
        key: "ad_group_name",
        compareKey: null,
        options: {
          metric: {
            id: "ad_group_name",
            name: "Ad Group Name",
            format: "text",
          },
          hideFilter: true,
          overflowCell: true,
          additionalStyles: { paddingTop: "20px" },
          formatter: (value: string) => value,
        },
      },
      {
        key: "target_bid",
        compareKey: null,
        options: {
          columnTitle: "Bid",
          className: "text-end",
          overrideKey: "manual_bid",
          strikeThroughText: (row: unknown) => {
            const typedRow = row as TableRow<ExpanderQueryData>;
            const state = typedRow.original.state;

            if (typeof state !== "string") return "";

            if (state.toLowerCase().includes("negative")) {
              return "text-decoration-line-through";
            }

            return "";
          },
        },
      },
      {
        key: "target_source",
        compareKey: null,
        options: {
          hideFilter: true,
          formatter: (value: string) =>
            value === "GV_DISCOVERY" ? "initial"
            : value === "harvested" ? "harvesting"
            : snakeCaseToSentenceCase(value).toLowerCase(),
        },
      },
      {
        key: "creation_date",
        compareKey: null,
        options: {
          metric: {
            id: "creation_date",
            accessor: (d: { creation_date?: string | null }) =>
              d.creation_date ? new Date(d.creation_date).getTime() : null,
            name: "Created On",
          },
          formatter: (v: string | null | undefined) => v ?? "",
          hideFilter: true,
          checked: false,
        },
      },

      {
        key: "match_type",
        compareKey: null,
        options: {
          width: 150,
          hideFilter: true,
          formatter: (v: string | null | undefined) => {
            return snakeCaseToSentenceCase(v).toLowerCase().includes("negative") ?
                snakeCaseToSentenceCase(v).toLowerCase().replace("negative", "negative ")
              : snakeCaseToSentenceCase(v).toLowerCase();
          },
        },
      },
      {
        key: "target_state",
        compareKey: null,
        options: {
          checked: false,
          metric: {
            id: "target_state",
            accessor: (d: { target_state: string }) => {
              const value = parseFloat(d.target_state);
              return isNaN(value) ? null : value;
            },
            name: "State",
            options: { checked: true },
          },
          formatter: (v: string) => snakeCaseToSentenceCase(v).toLowerCase(),
          filterType: "select",
          hideFilter: true,
        },
      },

      { key: "impressions", compareKey: "impressions_percent" },
      { key: "clicks", compareKey: "clicks_percent" },
      { key: "orders", compareKey: "conversion_percent" },
      { key: "ad_spend", compareKey: "ad_spend_percent" },
      { key: "ad_sales", compareKey: "ad_sales_percent" },
      perfCol,
      { key: "cpc", compareKey: "cpc_delta" },
      { key: "conversion_rate", compareKey: "conversion_rate_delta" },
      { key: "ctr", compareKey: "ctr_delta" },
      { key: "viewable_impressions", compareKey: "viewable_impressions_percent" },
      { key: "viewability_rate", compareKey: "viewability_rate_delta" },
      { key: "view_click_through_rate", compareKey: "view_click_through_rate_delta" },
    ];
    return cols;
  }, [acosOrRoas]);
  const tableId = dims === "query" ? `${context}-str-report` : `${context}-str`;
  const filterProps = useCustomTableFilters(tableId);
  const adsMapping: { [key: string]: GVAdvertisement } = useMemo(() => {
    return (advertisementData?.adplans ?? []).reduce(
      (acc: { [key: string]: GVAdvertisement }, row: GVAdvertisement) => {
        acc[row.ad_id.toString()] = row;
        return acc;
      },
      {} as { [key: string]: GVAdvertisement },
    );
  }, [advertisementData?.adplans]);
  const tableData = useMemo(() => {
    const data = tab === "target" ? targetData : keywordData;
    return (data && typeof data === "object" && "keywords" in data ? (data as KeywordsResponse).keywords : []).map(
      (row) => {
        const ad = adsMapping[row.advertisement_id];
        return {
          ...row,
          ad_name: ad?.ad_name,
          managed: (ad?.managed ?? "").toLowerCase() === "yes",
        };
      },
    );
  }, [keywordData, targetData, adsMapping, tab]);

  const unmanagedAdIds = useMemo(() => {
    return tableData.filter((ad) => !ad.managed).map((ad) => ad.id);
  }, [tableData]);

  if (isLoading || isAdsDataLoading || isTargetDataLoading || isKeywordDataLoading) {
    return <Loading fullPage={false} height={"300px"} />;
  }
  return (
    <Row className="pt-3">
      <Col xs={12} md={6}>
        <h3
          className="pb-4 px-5"
          style={{
            fontSize: 22,
          }}
        >
          {query} {keyword_text} {match_type ? `(${match_type})` : ""}
        </h3>
      </Col>
      <Col xs={12}>
        <div className={css({ paddingLeft: "large", paddingRight: "large" })}>
          <Col xs={12}>
            <MultiMetricChart
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              data={data}
              group={group}
              handleLegendChange={(e) => {
                const id = e.target.options.custom?.id;
                if (id) toggleMetric(id);
                return false;
              }}
              showAnnotations={false}
            />
          </Col>
          <Col className="ph-5 mb-5 border-bottom fs-standard" xs={12}>
            <Nav
              variant="underline"
              onSelect={(k) => {
                setTab(k);
              }}
              activeKey={tab ?? undefined}
            >
              <Nav.Item>
                <Nav.Link eventKey="keyword">Keywords</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="target">Targets</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
          {Array.isArray(tableData) && tableData.length > 0 ?
            <ExpanderTable
              tableRef={tableRef}
              tableId={tableId}
              isExpandable={false}
              isStatsLoading={Boolean(isKeywordDataLoading) || Boolean(isTargetDataLoading)}
              isSelectable={true}
              selectionKey={"target_id"}
              selected={selectedTargets}
              setSelected={setSelectedTargets}
              bulkActionOptions={[...bulkActionOptions, ...additionalBulkActions]}
              minRows={0}
              disableSelectionList={unmanagedAdIds}
              titleCol={{
                id: "target",
                Header: "Target",
                accessor: "text",
                style: { width: "300px" },
                width: 300,
                Cell: (props) => {
                  return (
                    <>
                      <KeywordCell
                        value={formatTargetText(props.original?.text, {
                          ...props.original?.addtl_data,
                          marketPlace: marketPlace,
                        })}
                        columnWidth={300}
                      />
                      <br />
                      <TrellisLink
                        to={getDashboardTo({
                          type: "advertisement",
                          id: props.original?.advertisement_id,
                        })}
                        target="_blank"
                        style={{
                          textDecoration: "none",
                          fontWeight: "500",
                          fontSize: "1.5rem",
                          cursor: "pointer",
                          paddingRight: "1rem",
                        }}
                      >
                        <small>{props.original?.ad_name}</small>
                      </TrellisLink>
                      <TargetBadges target={props.original} />
                    </>
                  );
                },
                Filter: () => <div />,
              }}
              data={tableData}
              columnSpecs={columns}
              getSearchCriteria={(row) => {
                const typedRow = row as {
                  text: string;
                  addtl_data: Record<string, unknown>;
                  query: string;
                  ad_name: string;
                  ad_group_name: string;
                };

                const formattedTargetText = formatTargetText(typedRow.text, {
                  ...typedRow.addtl_data,
                  marketPlace: marketPlace,
                });
                const { query, ad_name, ad_group_name } = typedRow;
                return `${query} ${ad_name} ${ad_group_name} ${formattedTargetText}`;
              }}
              tableRowDescription={"Search Terms"}
              defaultSorted={[{ id: "impressions", desc: true }]}
              isLoading={isKeywordDataLoading}
              filterWidget={true}
              hideFilterToggleIcon={true}
              hideResetFiltersIcon={false}
              excludedFilterColumns={["match_type"]}
              {...filterProps}
              additionalFilterOptions={[SEARCH_TERM_REPORT_MATCH_TYPE_FILTER]}
              handleBulkChanges={() => {}}
            />
          : <Flex
              className={css({
                padding: "large",
              })}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <p> There are no advertised {tab}s associated with this search query for the report's date range.</p>
            </Flex>
          }
        </div>
      </Col>
    </Row>
  );
};

export default QueryExpander;
